/* eslint-disable import/no-cycle */
import firebase from 'firebase/compat/app';
import { ConfigStore } from '../stores/config';
import { PrequalStore, usePrequal as getStorePrequal } from '../stores/prequalStore';
import {
  InternalAttribution,
  Marketing,
  PreQualUTM,
  Prequal,
  PrequalApplicationStatus,
  PrequalApprovalStatus,
  FullApplicationStatus,
  SellerType,
} from '../schema';
import {
  betterParseInt,
  betterParseFloat,
  sendCustomEventToIterable,
  formatIterablePayload,
} from '.';
import {
  isProduction,
  PROD_TRIDENT_API_URL,
  QA_TRIDENT_API_URL,
  BOAT_TRADER_SOURCE_ID,
  DEFAULT_PREQUAL_APR_DISCLAIMER,
  LEAD_SOURCE,
} from '../constants';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { getNumericQueryParam } from './queryParamHelper';
import { deleteCookie } from './cookies';
import { recordFinanceIdentity } from './recordFinanceIdentity';

export const addBoatDetails = () => {
  const params = new URLSearchParams(window.location.search);
  const boatInfo = params.get('boatInfo');

  if (boatInfo) {
    try {
      const boatDetails = JSON.parse(decodeURIComponent(boatInfo));
      return {
        imtId: boatDetails.imtId,
        partyId: boatDetails.partyId,
        purchasePrice: boatDetails.price,
        boatMake: boatDetails.make,
        boatModel: boatDetails.model,
        boatYear: boatDetails.year,
        boatType: boatDetails.type,
        boatLength: boatDetails.length,
        boatEngineCount: boatDetails.engineCount,
        boatEngineMake: boatDetails.engineMake,
        boatFuelType: boatDetails.fuelType,
        boatTotalPower: boatDetails.totalPower,
        sellerName: boatDetails.sellerName,
        sellerEmail: boatDetails.sellerEmail,
        sellerType: boatDetails.sellerType,
        sellerFinanceAdvantage: boatDetails.sellerFinanceAdvantage,
      };
    } catch (e) {
      console.error('Error parsing boatInfo:', e);
    }
  }
  return {};
};

const determineLoanType = (prequal: Prequal) => {
  const currentYear = new Date().getFullYear();
  if (prequal.imtId === 'NONBDP' || betterParseInt(prequal.boatYear) === currentYear) {
    return 'new';
  }

  return 'used';
};

/**
 * Generates a universally unique user ID (UUID).
 *
 * @return {string} The generated UUID.
 */
export const generateUUID = () => uuidv4();

export const isPrequalFunnel = () => window.location.pathname.includes('/pre-qualify');

export const formatEpochDate = (epochTime: number) => {
  const date = new Date(epochTime);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const getApprovalStatus = (rate: string): PrequalApprovalStatus =>
  rate === '-' || rate.toLowerCase() === 'call'
    ? PrequalApprovalStatus.notApproved
    : PrequalApprovalStatus.approved;

/**
 * Cleans the prequal results if Not Approved or an error occurs
 *
 * @param {Prequal} prequal - The prequal object.
 */
export const cleanPrequalApproval = (prequal: Prequal) => {
  delete prequal.prequalApprovalTerm;
  delete prequal.prequalApprovalRate;
};

/**
 * Determines the prequal status by making an API call to the /ratesCalculator endpoint.
 *
 * @param {Prequal} prequal - The prequal object.
 * @return {Promise<void>} A promise that resolves when the get request is complete.
 */
export const determinePrequalStatus = async (prequal: Prequal): Promise<void> => {
  const apiUrl = isProduction()
    ? `${PROD_TRIDENT_API_URL}/rates-calculator/0/0/Boat`
    : `${QA_TRIDENT_API_URL}/rates-calculator/0/0/Boat`;
  try {
    const response = await axios.get(apiUrl, {
      params: {
        loanType: determineLoanType(prequal),
        loanAmount: prequal.loanAmount,
        modelYear: prequal.boatYear || new Date().getFullYear(),
        ficoScoreRange: prequal.creditScore,
      },
    });

    const { monthlyPayment, rate, term }: { monthlyPayment: string; rate: string; term: string } =
      response.data;

    prequal.prequalApprovalStatus = getApprovalStatus(rate);

    if (prequal.prequalApprovalStatus === 'Not Approved') {
      cleanPrequalApproval(prequal);
      return;
    }

    prequal.prequalApprovalTerm = betterParseInt(term);
    prequal.prequalApprovalRate = betterParseFloat(rate);
    prequal.prequalApprovalMonthlyPayment = betterParseFloat(monthlyPayment);
  } catch (error) {
    console.error('Error determining prequal approval status:', error);
    prequal.prequalApprovalStatus = PrequalApprovalStatus.notApproved;
    cleanPrequalApproval(prequal);
  }
};

export const calculateTotalLoanAmount = (rate: number, term: number, monthlyPayment: number) => {
  const ratePerMonth = rate / 100 / 12;
  const x = (1 + ratePerMonth) ** term;
  const loanAmount =
    x * ratePerMonth === 0
      ? (monthlyPayment * term).toFixed(2)
      : ((monthlyPayment * (x - 1)) / (x * ratePerMonth)).toFixed(2);
  const totalPayments = (term * monthlyPayment).toFixed(2);
  const totalInterest = (Number(totalPayments) - Number(loanAmount)).toFixed(2);

  return {
    monthlyPayment,
    totalPayments,
    loanAmount,
    totalInterest,
  };
};

/**
 * Calculates purchase price for a desired monthly payment, term, and rate.
 * Makes an API call to the /rates endpoint to retrieve teaser rate
 * Uses 20 years as default term
 *
 * @param {Prequal} prequal - The prequal object.
 * @return {Promise<number>} A promise that resolves when the get request is complete.
 */
export const calculateBuyingPower = async (prequal: Prequal): Promise<number> => {
  const apiUrl = isProduction()
    ? `${PROD_TRIDENT_API_URL}/rates/0/0/Boat`
    : `${QA_TRIDENT_API_URL}/rates/0/0/Boat`;
  try {
    const response = await axios.get(apiUrl, {
      params: {
        loanType: determineLoanType(prequal),
      },
    });

    const { teaserRate = '7.12%', disclaimer9 = '' }: { teaserRate: string; disclaimer9: string } =
      response?.data?.[0] ?? {};

    prequal.prequalApprovalStatus = getApprovalStatus(teaserRate);
    prequal.prequalApprovalTerm = 240;
    prequal.prequalApprovalRate = betterParseFloat(teaserRate);
    prequal.aprDisclaimer = disclaimer9 ? disclaimer9.trim() : DEFAULT_PREQUAL_APR_DISCLAIMER;

    const { loanAmount } = calculateTotalLoanAmount(
      Number(prequal.prequalApprovalRate),
      prequal.prequalApprovalTerm,
      Number(prequal.prequalApprovalMonthlyPayment)
    );

    return Number(loanAmount) + Number(prequal.downPayment);
  } catch (error) {
    console.error('Error determining prequal approval status:', error);
    prequal.prequalApprovalStatus = PrequalApprovalStatus.notApproved;
    cleanPrequalApproval(prequal);
    return 0;
  }
};

/**
 * Prefers a fresh (current URL/session) value over a cached one, but never lets an
 * absent fresh value erase a value a prior step/visit already captured.
 */
const coalesce = <T>(
  fresh: T | null | undefined,
  cached: T | null | undefined
): T | null | undefined => (fresh === null || fresh === undefined ? cached : fresh);

// Attribution string values come straight from URL params: a param present but blank
// (e.g. ?utm_medium=) is treated as absent too, rather than stomping a real cached value.
const coalesceString = (
  fresh: string | null | undefined,
  cached: string | null | undefined
): string | null | undefined => coalesce(fresh || undefined, cached);

const coalesceUtm = (
  fresh: PreQualUTM | null | undefined,
  cached: Partial<PreQualUTM> | null | undefined
): PreQualUTM => ({
  utmCampaign: coalesceString(fresh?.utmCampaign, cached?.utmCampaign),
  utmContent: coalesceString(fresh?.utmContent, cached?.utmContent),
  utmId: coalesceString(fresh?.utmId, cached?.utmId),
  utmMedium: coalesceString(fresh?.utmMedium, cached?.utmMedium),
  utmName: coalesceString(fresh?.utmName, cached?.utmName),
  utmSource: coalesceString(fresh?.utmSource, cached?.utmSource),
  utmTerm: coalesceString(fresh?.utmTerm, cached?.utmTerm),
});

const coalesceInternal = (
  fresh: InternalAttribution | null | undefined,
  cached: Partial<InternalAttribution> | null | undefined
): InternalAttribution => ({
  internalCampaign: coalesceString(fresh?.internalCampaign, cached?.internalCampaign),
  internalContent: coalesceString(fresh?.internalContent, cached?.internalContent),
  internalId: coalesceString(fresh?.internalId, cached?.internalId),
  internalMedium: coalesceString(fresh?.internalMedium, cached?.internalMedium),
  internalName: coalesceString(fresh?.internalName, cached?.internalName),
  internalPosition: coalesceString(fresh?.internalPosition, cached?.internalPosition),
  internalSource: coalesceString(fresh?.internalSource, cached?.internalSource),
  internalTerm: coalesceString(fresh?.internalTerm, cached?.internalTerm),
});

const coalesceMarketing = (
  fresh: Marketing | null | undefined,
  cached: Partial<Marketing> | null | undefined
): Marketing => ({
  adGroup: coalesceString(fresh?.adGroup, cached?.adGroup),
  adGroupId: coalesce(fresh?.adGroupId, cached?.adGroupId),
  campaignId: coalesce(fresh?.campaignId, cached?.campaignId),
  campaignName: coalesceString(fresh?.campaignName, cached?.campaignName),
});

/**
 * Retrieves prequal data from local storage or generates new data based on prequalStore
 *
 * @param {PrequalStore} prequalStore - the PrequalStore object containing prequal data
 * @return {Prequal | {}} the retrieved or generated prequal data
 */
export const getPrequal = (prequalStore: PrequalStore) => {
  const { isBDP } = prequalStore;

  // Check if we're in LendAPI flow by looking for the URL parameter
  const isLendAPIFlow = new URLSearchParams(window.location.search).get('lendAPI') === 'true';

  let imtId: string;
  if (isLendAPIFlow) {
    // For LendAPI flow, use the saved imtId or fallback to NONBDP
    const savedLendApiImtId = localStorage.getItem('current-lendapi-prequal-imtid');
    if (savedLendApiImtId) {
      imtId = savedLendApiImtId;
    } else {
      imtId = 'NONBDP';
    }
  } else {
    imtId = prequalStore.imtId || 'NONBDP';
  }

  const storageKey = `prequal-${imtId}`;
  const storedPrequal = localStorage.getItem(storageKey);
  let loadedPrequal: Partial<Prequal>;

  if (storedPrequal) {
    // TRIDENT-995: a cached record from a prior visit must not silently override
    // this click's attribution (utm/internal) with stale or absent marketing data.
    // In-progress application state (loanAmount, ficoScore, etc.) is preserved as-is.
    const cachedPrequal: Partial<Prequal> = JSON.parse(storedPrequal);
    // prequalStore.source already backfills a URL with no `source` param to
    // BOAT_TRADER_SOURCE_ID, so it never looks "absent" to coalesce(). Read the raw
    // param instead so a returning visitor without `source` in the URL keeps their
    // cached source instead of being reassigned to BOAT_TRADER_SOURCE_ID.
    const freshSourceParam = getNumericQueryParam({ param: 'source' });
    loadedPrequal = {
      ...cachedPrequal,
      source: coalesce(freshSourceParam, cachedPrequal.source) ?? BOAT_TRADER_SOURCE_ID,
      subsource: coalesce(prequalStore.subsource, cachedPrequal.subsource),
      utm: coalesceUtm(prequalStore?.utm, cachedPrequal?.utm),
      internal: coalesceInternal(prequalStore?.internal, cachedPrequal?.internal),
      marketing: coalesceMarketing(prequalStore?.marketing, cachedPrequal?.marketing),
      keyCode: coalesceString(prequalStore?.keyCode, cachedPrequal?.keyCode),
      promoCode: coalesceString(prequalStore?.promoCode, cachedPrequal?.promoCode),
      repCode: coalesceString(prequalStore?.repCode, cachedPrequal?.repCode),
      sourceLabel: coalesceString(prequalStore?.sourceLabel, cachedPrequal?.sourceLabel),
      subSourceLabel: coalesceString(prequalStore?.subSourceLabel, cachedPrequal?.subSourceLabel),
      variant: coalesceString(prequalStore?.variant, cachedPrequal?.variant),
      kamExpVariant: coalesceString(prequalStore?.kamExpVariant, cachedPrequal?.kamExpVariant),
    };
  } else {
    if (isBDP) {
      loadedPrequal = addBoatDetails();
    } else {
      loadedPrequal = { imtId: 'NONBDP' };
    }

    loadedPrequal = {
      ...loadedPrequal,
      source: prequalStore.source || BOAT_TRADER_SOURCE_ID,
      subsource: prequalStore.subsource,
      prequalApplicationId: prequalStore.prequalApplicationId,
      utm: {
        utmCampaign: prequalStore?.utm?.utmCampaign,
        utmContent: prequalStore?.utm?.utmContent,
        utmId: prequalStore?.utm?.utmId,
        utmMedium: prequalStore?.utm?.utmMedium,
        utmName: prequalStore?.utm?.utmName,
        utmSource: prequalStore?.utm?.utmSource,
        utmTerm: prequalStore?.utm?.utmTerm,
      },
      internal: {
        internalCampaign: prequalStore?.internal?.internalCampaign,
        internalContent: prequalStore?.internal?.internalContent,
        internalId: prequalStore?.internal?.internalId,
        internalMedium: prequalStore?.internal?.internalMedium,
        internalName: prequalStore?.internal?.internalName,
        internalPosition: prequalStore?.internal?.internalPosition,
        internalSource: prequalStore?.internal?.internalSource,
        internalTerm: prequalStore?.internal?.internalTerm,
      },
      leadPage: prequalStore.leadPage,
      leadOrigin: prequalStore?.leadOrigin,
      phone: null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      prequalApplicationStatus: PrequalApplicationStatus.started,
      prequalApprovalStatus: null,
      prequalApprovalTerm: null,
      prequalApprovalRate: null,
      prequalApprovalMonthlyPayment: null,
      sevenHundredCreditPullTransactionId: null,
      sevenHundredCreditPullHasScore: null,
      fullApplicationId: null,
      fullApplicationStatus: FullApplicationStatus.notStarted,
      fullApplicationStartedAt: null,
      leadUUID: null,
      leadEmail: null,
      leadApprovalStatus: null,
      leadCreatedAt: null,
      completedAt: null,
      marketing: prequalStore?.marketing || null,
      keyCode: prequalStore?.keyCode,
      promoCode: prequalStore?.promoCode,
      repCode: prequalStore?.repCode,
      sourceLabel: prequalStore?.sourceLabel,
      subSourceLabel: prequalStore?.subSourceLabel,
      variant: prequalStore?.variant,
      kamExpVariant: prequalStore?.kamExpVariant,
    };

    localStorage.setItem(storageKey, JSON.stringify(loadedPrequal));
  }

  return loadedPrequal;
};

/**
 * Saves the prequal data to local storage using the IMT ID as the storage key.
 * IMT ID can be 'NONBDP' if the prequal is not from BDP
 *
 * @param {Prequal} prequal - the prequal data to be saved
 * @return {void}
 */
export const savePrequal = (prequal: Prequal) => {
  const storageKey = `prequal-${prequal.imtId}`;
  prequal.updatedAt = Date.now();
  localStorage.setItem(storageKey, JSON.stringify(prequal));
};

export const camelCaseMarketingParam = (name: string) => {
  let camelCasedName = '';
  const paramName = name.toLowerCase();

  switch (paramName) {
    case 'adgroup':
      camelCasedName = 'adGroup';
      break;
    case 'adgroupid':
      camelCasedName = 'adGroupId';
      break;
    case 'campaignid':
      camelCasedName = 'campaignId';
      break;
    case 'campaign':
      camelCasedName = 'campaignName';
      break;
    default:
      camelCasedName = paramName;
  }

  return camelCasedName;
};

export const getSource = (config: ConfigStore | null, prequal: Partial<Prequal>): number => {
  let sourceId: number | undefined = BOAT_TRADER_SOURCE_ID;

  if (config) {
    const sourceIdParam = getNumericQueryParam({ param: 'source' });

    if (prequal?.source) {
      sourceId = prequal.source;
    } else if (sourceIdParam) {
      sourceId = sourceIdParam;
    } else {
      const source = config.getSourceFromLocation(window.location.href);
      sourceId = source && source.id === 0 ? 0 : source?.id || sourceId;
    }
  }

  return sourceId;
};

export const prepareEventDataToIterable = (castedPrequal: Prequal, prequal: Prequal) => {
  const source = prequal?.source ? LEAD_SOURCE.get(prequal.source) ?? 'Other' : 'Other';
  const payload = formatIterablePayload(castedPrequal, {
    applicationStatus: PrequalApplicationStatus.submitted,
    approvalStatus: prequal.prequalApprovalStatus,
    startedAt: prequal.createdAt ? formatEpochDate(prequal.createdAt) : undefined,
    completedAt: prequal.completedAt ? formatEpochDate(prequal.completedAt) : undefined,
    prequalDisclaimer: prequal?.aprDisclaimer ?? DEFAULT_PREQUAL_APR_DISCLAIMER,
    source,
    ...(castedPrequal.imtId === 'NONBDP' && { boatImtId: null }),
    ...(prequal.prequalApprovalStatus === PrequalApprovalStatus.approved && {
      desiredMonthlyPayment: prequal.prequalApprovalMonthlyPayment,
      downPayment: prequal.downPayment,
      loanAmount: prequal.loanAmount,
      term: prequal.prequalApprovalTerm,
      interestRate: prequal.prequalApprovalRate,
      purchasingPower: prequal.purchasePrice,
    }),
  });
  return payload;
};

export const completePrequalFunnelV1 = async () => {
  const prequalStore = getStorePrequal();
  const { prequal } = prequalStore;
  const castedPrequal = prequal as Prequal;
  const purchasePrice = await calculateBuyingPower(castedPrequal);
  const functions = firebase.functions();
  const completePrequal = functions.httpsCallable('completePrequal');
  prequalStore.setPurchasePrice({ purchasePrice: purchasePrice.toString() });
  prequalStore.setDownPayment({
    downPayment: castedPrequal.downPayment.toString(),
  });
  const [completePrequalResult] = await Promise.allSettled([
    completePrequal({ prequal: castedPrequal }),
    sendCustomEventToIterable({
      eventData: prepareEventDataToIterable(castedPrequal, prequal as Prequal),
      email: prequal.email as string,
      eventName: 'PreQualCompleted',
    }),
  ]);
  if (completePrequalResult.status === 'fulfilled') {
    recordFinanceIdentity('lead');
  }
  deleteCookie('utmParamsCookie');
};

export const completePrequalFunnelV2 = async () => {
  const prequalStore = getStorePrequal();
  const { prequal } = prequalStore;
  const castedPrequal = prequal as Prequal;
  const purchasePrice = await calculateBuyingPower(castedPrequal);
  const functions = firebase.functions();
  const completePrequal = functions.httpsCallable('completePrequalV2');

  prequalStore.setPurchasePrice({ purchasePrice: purchasePrice.toString() });
  prequalStore.setDownPayment({
    downPayment: castedPrequal.downPayment.toString(),
  });
  const savedPrequalApprovalStatus = prequal.prequalApprovalStatus;
  const [completePrequalResult] = await Promise.allSettled([
    completePrequal({ prequal: castedPrequal }),
    sendCustomEventToIterable({
      email: prequal.email as string,
      eventName: 'PreQualCompleted',
      eventData: prepareEventDataToIterable(castedPrequal, prequal as Prequal),
    }),
  ]);
  if (completePrequalResult.status === 'fulfilled') {
    recordFinanceIdentity('lead');
    const { data } = completePrequalResult.value;
    prequal.prequalApprovalStatus = data.prequalApprovalStatus;
    prequal.leadEmail = data.prequal.leadEmail;
    prequal.leadApprovalStatus = data.prequal.leadApprovalStatus;
    prequal.leadCreatedAt = data.prequal.leadCreatedAt;
    prequal.sevenHundredCreditPullHasScore = data.prequal?.sevenHundredCreditPullHasScore;
    prequal.sevenHundredCreditPullTransactionId = data.prequal?.sevenHundredCreditPullTransactionId;
    // Able to pull credit and run prequal and user is approved: update prequal results
    if (data.success && data.prequalApprovalStatus === PrequalApprovalStatus.approved) {
      prequal.prequalApprovalTerm = data.prequal.prequalApprovalTerm;
      prequal.prequalApprovalRate = data.prequal.prequalApprovalRate;
      prequal.prequalApprovalMonthlyPayment = data.prequal.prequalApprovalMonthlyPayment;
      prequal.loanAmount = data.prequal.loanAmount;
      prequal.purchasePrice = data.prequal.purchasePrice;
      prequal.creditScore = data.prequal.creditScore;
    }
    // Able to pull credit and run prequal and user not approved: cleanup prequal results
    if (data.success && data.prequalApprovalStatus === PrequalApprovalStatus.notApproved) {
      cleanPrequalApproval(prequal as Prequal);
    }
    // Failed to pull credit or run prequal: restore prequal results using self selected fico
    if (!data.success) {
      prequal.prequalApprovalStatus = savedPrequalApprovalStatus;
    }
  }
  deleteCookie('utmParamsCookie');
};
